import { useEffect, useMemo, useRef, useState } from 'react'

import Modal from 'react-bootstrap/Modal'
import Button from 'react-bootstrap/Button'
import Form from 'react-bootstrap/Form'
import Table from 'react-bootstrap/Table'
import Alert from 'react-bootstrap/Alert'
import Stack from 'react-bootstrap/Stack'

import {
  bulkSendReservedPackageInvoices,
  getBulkReservedInvoiceSendStatus,
  getCustomFees,
  listReservedPackageInvoiceHolders,
} from '../../../utilities/api'
import { formatCurrency, getProcessingFee } from '../../../utilities/helpers'
import AuthService from '../../../utilities/services/auth.service'
import { LoadingContainer } from '../../LoadingContainer'
import {
  clearStoredInvoiceSendBatchId,
  readStoredInvoiceSendBatchId,
  startBulkReservedInvoiceSendPoll,
  storeInvoiceSendBatchId,
} from './pollBulkReservedInvoiceSend'
import './bulkSendReservedPackageInvoices.scss'

function normalizeCustomFee(entry) {
  if (!entry) return null
  const attrs = entry.attributes || entry
  const id = Number(entry.id)
  if (!Number.isFinite(id) || id <= 0) return null
  return {
    id,
    name: attrs.name || `Fee ${id}`,
    option: String(attrs.option || 'amount').toLowerCase(),
    feeAmount: Number(attrs.feeAmount),
  }
}

function resolveCustomFeeLineAmount(fee, percentBase) {
  const raw = Number(fee?.feeAmount)
  if (!Number.isFinite(raw) || raw <= 0) return 0
  if (fee?.option === 'percent') {
    return Number((((Number(percentBase) || 0) * raw) / 100).toFixed(2))
  }
  return Number(raw.toFixed(2))
}

function holderInvoiceAmount(holder, customFees) {
  const fees = Array.isArray(customFees)
    ? customFees
    : customFees
      ? [customFees]
      : []
  const base = Number(holder?.amount) || 0
  const serviceFee = Number(holder?.serviceFee) || 0
  const alreadyApplied = new Set(
    (holder?.existingCustomFeeIds || [])
      .map((id) => Number(id))
      .filter((id) => Number.isFinite(id))
  )
  const feeTotal = fees.reduce((sum, fee) => {
    const id = Number(fee?.id)
    if (!Number.isFinite(id) || alreadyApplied.has(id)) return sum
    return sum + resolveCustomFeeLineAmount(fee, base)
  }, 0)
  const withCustomFee = Number((base + feeTotal).toFixed(2))
  const withServiceFee = Number((withCustomFee + serviceFee).toFixed(2))
  const processingFee = Number(
    (getProcessingFee('invoice', withServiceFee) || 0).toFixed(2)
  )
  return Number((withServiceFee + processingFee).toFixed(2))
}

function feeOptionLabel(fee) {
  if (!Number.isFinite(fee?.feeAmount)) return fee?.name || 'Custom fee'
  if (fee.option === 'percent') return `${fee.name} (${fee.feeAmount}%)`
  return `${fee.name} (${formatCurrency(fee.feeAmount)})`
}

function invoiceFeeNames(result) {
  const fees = Array.isArray(result?.customFees)
    ? result.customFees
    : result?.customFee
      ? [result.customFee]
      : []
  return fees.map((fee) => fee?.name).filter(Boolean)
}

function seatItems(seats) {
  if (!seats) return []
  return String(seats)
    .split(',')
    .map((seat) => seat.trim())
    .filter(Boolean)
}

function SeatList({ seats }) {
  const items = seatItems(seats)
  if (!items.length) return '—'

  return (
    <div className="bulk-send-reserved-invoices__seats-list">
      {items.map((seat, index) => (
        <span
          key={`${seat}-${index}`}
          className="bulk-send-reserved-invoices__seat"
        >
          {seat}
          {index < items.length - 1 ? ',' : ''}
        </span>
      ))}
    </div>
  )
}

export default function BulkSendReservedPackageInvoices({
  show,
  onHide,
  packageUUID,
  onSuccess,
}) {
  const organization = AuthService.getOrg()
  const organizationUUID = organization?.[0]?.uuid

  const [loading, setLoading] = useState(true)
  const [sending, setSending] = useState(false)
  const [error, setError] = useState('')
  const [holders, setHolders] = useState([])
  const [selectedIds, setSelectedIds] = useState(() => new Set())
  const [searchTerm, setSearchTerm] = useState('')
  const [result, setResult] = useState(null)
  const [progress, setProgress] = useState(null)
  const [customFees, setCustomFees] = useState([])
  const [feeRows, setFeeRows] = useState([])
  const stopPollRef = useRef(null)
  const onSuccessRef = useRef(onSuccess)
  onSuccessRef.current = onSuccess

  const watchSend = (batchId) => {
    stopPollRef.current?.()
    storeInvoiceSendBatchId(packageUUID, batchId)
    stopPollRef.current = startBulkReservedInvoiceSendPoll(batchId, {
      fetchStatus: getBulkReservedInvoiceSendStatus,
      onProgress: (job) => {
        setProgress(job)
        setSending(true)
      },
      onCompleted: (job) => {
        clearStoredInvoiceSendBatchId(packageUUID)
        setProgress(null)
        setSending(false)
        setResult(job || {})
        try {
          onSuccessRef.current?.()
        } catch (callbackErr) {
          console.warn(
            'Bulk reserved invoices sent but onSuccess callback failed:',
            callbackErr
          )
        }
      },
      onFailed: (job) => {
        clearStoredInvoiceSendBatchId(packageUUID)
        setProgress(null)
        setSending(false)
        if (job?.sent || job?.failed || job?.skipped) setResult(job)
        setError(job?.error || 'Could not send invoices.')
      },
    })
  }

  useEffect(() => {
    if (!show) return undefined

    let cancelled = false
    const loadHolders = async () => {
      const storedBatchId = readStoredInvoiceSendBatchId(packageUUID)
      setLoading(true)
      setError('')
      setSearchTerm('')
      setFeeRows([])
      if (!storedBatchId) {
        setResult(null)
        setProgress(null)
        setSending(false)
      }
      try {
        const [holdersRes, feesRes] = await Promise.all([
          listReservedPackageInvoiceHolders({
            packageUUID,
            organizationUUID,
          }),
          getCustomFees(organizationUUID).catch(() => ({ data: { data: [] } })),
        ])
        if (cancelled) return
        const rows = holdersRes?.data?.holders || []
        setHolders(rows)
        setSelectedIds(
          new Set(
            rows
              .filter((holder) => holder.canEmail)
              .map((holder) => holder.orderId)
          )
        )
        setCustomFees(
          (feesRes?.data?.data || []).map(normalizeCustomFee).filter(Boolean)
        )
        if (storedBatchId) {
          try {
            const statusRes =
              await getBulkReservedInvoiceSendStatus(storedBatchId)
            if (cancelled) return
            const job = statusRes?.data
            if (job?.status === 'completed') {
              clearStoredInvoiceSendBatchId(packageUUID)
              setProgress(null)
              setSending(false)
              setResult(job)
              try {
                onSuccessRef.current?.()
              } catch (callbackErr) {
                console.warn(
                  'Bulk reserved invoices sent but onSuccess callback failed:',
                  callbackErr
                )
              }
            } else if (job?.status === 'failed') {
              clearStoredInvoiceSendBatchId(packageUUID)
              setProgress(null)
              setSending(false)
              if (job?.sent || job?.failed || job?.skipped) setResult(job)
              setError(job?.error || 'Could not send invoices.')
            } else {
              setResult(null)
              setProgress(job)
              setSending(true)
              watchSend(storedBatchId)
            }
          } catch (statusErr) {
            if (!cancelled && statusErr?.response?.status === 404) {
              clearStoredInvoiceSendBatchId(packageUUID)
            }
          }
        }
      } catch (err) {
        if (!cancelled) {
          setError(
            err?.response?.data?.error?.message ||
              err?.response?.data?.message ||
              err?.message ||
              'Could not load reserved package holders.'
          )
          setHolders([])
          setSelectedIds(new Set())
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    }

    if (!packageUUID || !organizationUUID) {
      setError('Package and organization are required.')
      setLoading(false)
      setHolders([])
      return undefined
    }

    loadHolders()
    return () => {
      cancelled = true
      stopPollRef.current?.()
    }
  }, [show, packageUUID, organizationUUID])

  const visibleHolders = useMemo(() => {
    const query = searchTerm.trim().toLowerCase()
    if (!query) return holders
    return holders.filter((holder) =>
      [holder.name, holder.email, holder.seats]
        .join(' ')
        .toLowerCase()
        .includes(query)
    )
  }, [holders, searchTerm])

  const selectedFees = useMemo(
    () =>
      feeRows
        .map((id) => customFees.find((fee) => String(fee.id) === String(id)))
        .filter(Boolean),
    [customFees, feeRows]
  )
  const selectedFeeIds = useMemo(
    () => new Set(selectedFees.map((fee) => String(fee.id))),
    [selectedFees]
  )
  const selectedCount = selectedIds.size
  const emailableCount = holders.filter((holder) => holder.canEmail).length
  const allVisibleSelected =
    visibleHolders.length > 0 &&
    visibleHolders
      .filter((holder) => holder.canEmail)
      .every((holder) => selectedIds.has(holder.orderId))

  const toggleHolder = (orderId, canEmail) => {
    if (!canEmail) return
    setSelectedIds((current) => {
      const next = new Set(current)
      if (next.has(orderId)) next.delete(orderId)
      else next.add(orderId)
      return next
    })
  }

  const toggleVisible = (checked) => {
    setSelectedIds((current) => {
      const next = new Set(current)
      visibleHolders.forEach((holder) => {
        if (!holder.canEmail) return
        if (checked) next.add(holder.orderId)
        else next.delete(holder.orderId)
      })
      return next
    })
  }

  const handleSend = async () => {
    if (!selectedCount) return
    setSending(true)
    setError('')
    try {
      const res = await bulkSendReservedPackageInvoices({
        packageUUID,
        organizationUUID,
        orderIds: [...selectedIds],
        ...(selectedFees.length
          ? { customFeeIds: selectedFees.map((fee) => fee.id) }
          : {}),
      })
      const batchId = res?.data?.batchId
      if (res?.status === 202 || batchId) {
        setProgress({
          status: 'queued',
          requested: res?.data?.requested ?? selectedCount,
          sent: 0,
          failed: 0,
          skipped: 0,
        })
        watchSend(batchId)
        return
      }
      setResult(res?.data || {})
      setSending(false)
      try {
        onSuccess?.()
      } catch (callbackErr) {
        console.warn(
          'Bulk reserved invoices sent but onSuccess callback failed:',
          callbackErr
        )
      }
    } catch (err) {
      setSending(false)
      setError(
        err?.response?.data?.error?.message ||
          err?.response?.data?.message ||
          err?.message ||
          'Could not send invoices.'
      )
    }
  }

  const updateFeeRow = (index, nextId) => {
    setFeeRows((rows) => {
      if (!nextId) return rows.filter((_, rowIndex) => rowIndex !== index)
      const next = rows.slice()
      next[index] = nextId
      return next
    })
  }

  const handleClose = () => {
    onHide()
  }

  return (
    <Modal
      show={show}
      onHide={handleClose}
      size="xl"
      centered
      className="bulk-send-reserved-invoices"
    >
      <Modal.Header closeButton>
        <Modal.Title>Send invoices to reserved holders</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="text-muted">
          Every reserved season ticket holder is selected. Uncheck anyone who
          should not receive an invoice. Optionally add custom fees, such as a
          package donation, to every selected invoice. Amount due includes the
          organization service fee and invoice processing fee. Sending converts
          each reserved hold into an open invoice due in 30 days.
        </p>

        {loading ? (
          <div
            className="bulk-send-reserved-invoices__loading"
            role="status"
            aria-live="polite"
          >
            <LoadingContainer>
              <span className="text-muted">Loading reserved holders…</span>
            </LoadingContainer>
          </div>
        ) : (
          <>
            {error ? <Alert variant="danger">{error}</Alert> : null}

            {sending && !result ? (
              <Alert variant="info">
                Sending {progress?.requested ?? selectedCount}{' '}
                {(progress?.requested ?? selectedCount) === 1
                  ? 'invoice'
                  : 'invoices'}
                . {progress?.sent || 0} sent
                {progress?.failed ? `, ${progress.failed} failed` : ''}. You can
                close this window. Sending continues in the background.
              </Alert>
            ) : null}

            {result ? (
              <Alert
                variant={
                  result.failed || result.skipped ? 'warning' : 'success'
                }
              >
                <div>
                  Sent {result.sent || 0}{' '}
                  {result.sent === 1 ? 'invoice' : 'invoices'}
                  {invoiceFeeNames(result).length
                    ? ` with ${invoiceFeeNames(result).join(', ')}`
                    : ''}
                  {result.skipped
                    ? `. Skipped ${result.skipped} without an email or matching reserved order.`
                    : ''}
                  {result.failed ? ` ${result.failed} failed.` : ''}
                </div>
                {result.failures?.length ? (
                  <ul className="mb-0 mt-2 small">
                    {result.failures.slice(0, 8).map((failure) => (
                      <li key={failure.orderId}>
                        Order {failure.orderId}
                        {failure.email ? ` (${failure.email})` : ''}:{' '}
                        {failure.error}
                      </li>
                    ))}
                    {result.failures.length > 8 ? (
                      <li>and {result.failures.length - 8} more</li>
                    ) : null}
                  </ul>
                ) : null}
              </Alert>
            ) : null}

            {!holders.length && !error ? (
              <Alert variant="info">
                There are no reserved package holders to invoice.
              </Alert>
            ) : null}

            {holders.length > 0 ? (
              <>
                <div className="d-flex flex-wrap gap-3 align-items-end mb-3">
                  <Form.Group className="flex-grow-1">
                    <Form.Label htmlFor="reserved-invoice-search">
                      Search holders
                    </Form.Label>
                    <Form.Control
                      id="reserved-invoice-search"
                      type="search"
                      value={searchTerm}
                      onChange={(event) => setSearchTerm(event.target.value)}
                      placeholder="Search by name, email, or seat"
                      disabled={sending}
                    />
                  </Form.Group>
                  <div className="text-muted pb-2">
                    {selectedCount} of {emailableCount} selected
                  </div>
                </div>

                {customFees.length > 0 ? (
                  <div className="bulk-send-reserved-invoices__fees mb-3">
                    <div className="fw-semibold mb-2">Custom fees</div>
                    <Stack gap={2}>
                      {feeRows.map((rowId, index) => (
                        <Stack
                          key={`${rowId || 'new'}-${index}`}
                          direction="horizontal"
                          gap={2}
                          className="bulk-send-reserved-invoices__fee-row"
                        >
                          <Form.Select
                            aria-label={`Custom fee ${index + 1}`}
                            value={rowId}
                            onChange={(event) =>
                              updateFeeRow(index, event.target.value)
                            }
                            disabled={sending}
                          >
                            <option value="">Select a custom fee</option>
                            {customFees.map((fee) => {
                              const taken =
                                String(fee.id) !== String(rowId) &&
                                selectedFeeIds.has(String(fee.id))
                              if (taken) return null
                              return (
                                <option key={fee.id} value={fee.id}>
                                  {feeOptionLabel(fee)}
                                </option>
                              )
                            })}
                          </Form.Select>
                          <Button
                            variant="outline-danger"
                            onClick={() =>
                              setFeeRows((rows) =>
                                rows.filter((_, rowIndex) => rowIndex !== index)
                              )
                            }
                            disabled={sending}
                          >
                            Remove
                          </Button>
                        </Stack>
                      ))}
                      <div>
                        <Button
                          variant="outline-light"
                          onClick={() => setFeeRows((rows) => [...rows, ''])}
                          disabled={
                            sending ||
                            feeRows.some((id) => !id) ||
                            feeRows.length >= customFees.length
                          }
                        >
                          Add custom fee
                        </Button>
                      </div>
                    </Stack>
                  </div>
                ) : null}

                <div className="table-responsive" style={{ maxHeight: 420 }}>
                  <Table
                    hover
                    size="sm"
                    className="align-top mb-0 bulk-send-reserved-invoices__table"
                  >
                    <colgroup>
                      <col style={{ width: '3rem' }} />
                      <col style={{ width: '20%' }} />
                      <col style={{ width: '30%' }} />
                      <col />
                      <col style={{ width: '7.5rem' }} />
                    </colgroup>
                    <thead className="sticky-top bg-white">
                      <tr>
                        <th className="w-content">
                          <Form.Check
                            aria-label="Select all visible reserved holders"
                            checked={allVisibleSelected}
                            disabled={sending}
                            onChange={(event) =>
                              toggleVisible(event.target.checked)
                            }
                          />
                        </th>
                        <th className="text-start">Name</th>
                        <th className="text-start">Email</th>
                        <th className="text-start">Seats</th>
                        <th className="text-end bulk-send-reserved-invoices__amount">
                          Amount due
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {visibleHolders.map((holder) => (
                        <tr key={holder.orderId}>
                          <td>
                            <Form.Check
                              aria-label={`Include ${holder.name || holder.email} in invoice send`}
                              checked={selectedIds.has(holder.orderId)}
                              disabled={sending || !holder.canEmail}
                              onChange={() =>
                                toggleHolder(holder.orderId, holder.canEmail)
                              }
                            />
                          </td>
                          <td className="bulk-send-reserved-invoices__name text-start text-wrap">
                            {holder.name}
                          </td>
                          <td className="bulk-send-reserved-invoices__email text-start text-wrap text-break">
                            {holder.email || (
                              <span className="text-danger">Missing email</span>
                            )}
                          </td>
                          <td className="bulk-send-reserved-invoices__seats text-start text-wrap">
                            <SeatList seats={holder.seats} />
                          </td>
                          <td className="bulk-send-reserved-invoices__amount">
                            {formatCurrency(
                              holderInvoiceAmount(holder, selectedFees)
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              </>
            ) : null}
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Stack direction="horizontal" gap={2} className="ms-auto">
          <Button variant="outline-light" size="lg" onClick={handleClose}>
            {result || sending ? 'Close' : 'Cancel'}
          </Button>
          {!result && holders.length > 0 ? (
            <Button
              variant="primary"
              size="lg"
              onClick={handleSend}
              disabled={sending || selectedCount === 0}
            >
              {sending
                ? 'Sending invoices…'
                : `Send ${selectedCount} ${
                    selectedCount === 1 ? 'invoice' : 'invoices'
                  }${
                    selectedFees.length
                      ? ` with ${selectedFees.map((fee) => fee.name).join(', ')}`
                      : ''
                  }`}
            </Button>
          ) : null}
        </Stack>
      </Modal.Footer>
    </Modal>
  )
}
